import {
  MenuItemProps,
  MenuItemType,
  MenuSection,
} from "components/ui/Menu/types";
import { ApiCardAccount } from "utils/api/cardAccount";
import { ApiIncomeTaxReturn } from "utils/api/incomeTaxReturns";
import { ApiUserProfile } from "utils/api/user";

interface Props {
  userProfile: ApiUserProfile;
  incomeTaxReturns: ApiIncomeTaxReturn[];
}

const getSettingsSection = (userProfile: ApiUserProfile): MenuItemProps[] => {
  let section: MenuItemProps[] = [
    {
      type: MenuItemType.routerLink,
      children: "Personal info",
      icon: "userCircle",
      data: {
        to: "/account/personal-info",
      },
    },
  ];

  if (userProfile.phoneNumber) {
    section = [
      ...section,
      {
        type: MenuItemType.routerLink,
        children: "Login & security",
        icon: "shield",
        data: {
          to: "/account/security",
        },
      },
      {
        type: MenuItemType.routerLink,
        children: "Text message settings",
        icon: "commentSms",
        data: {
          to: "/account/text-message-settings",
        },
      },
    ];
  }

  return section;
};

const hasPhysicalCard = (cardAccount: ApiCardAccount) =>
  cardAccount.cards.some((card) => card.form === "PHYSICAL");

const getCardSection = (userProfile: ApiUserProfile): MenuItemProps[] => {
  let section: MenuItemProps[] = [];

  if (userProfile.currentCardAccount) {
    section = [
      ...section,
      {
        type: MenuItemType.routerLink,
        children: "Deposit account statements",
        icon: "invoice",
        data: {
          to: "/account/statements",
        },
      },
    ];

    if (hasPhysicalCard(userProfile.currentCardAccount)) {
      section = [
        ...section,
        {
          type: MenuItemType.routerLink,
          children: "Replace card",
          icon: "mailbox",
          data: {
            to: "/account/replace-card",
          },
        },
      ];
    }
  } else {
    section = [
      ...section,
      {
        type: MenuItemType.link,
        children: "Apply now",
        icon: "creditCard",
        isCta: true,
        data: {
          href: "/apply",
        },
      },
    ];

    if (userProfile.hasFboAccount) {
      section.push({
        type: MenuItemType.routerLink,
        children: "Set up direct deposit",
        icon: "bank",
        data: {
          to: "/account/fbo-account",
        },
      });
    }
  }

  return section;
};

const getSupportSection = (
  incomeTaxReturns: ApiIncomeTaxReturn[]
): MenuItemProps[] => {
  const items = [];

  if (incomeTaxReturns.length > 0) {
    items.push({
      type: MenuItemType.routerLink,
      children: "Tax returns",
      icon: "calculator",
      data: {
        to: "/tax-return",
      },
    });
  }

  items.push(
    {
      type: MenuItemType.routerLink,
      children: "Documents",
      icon: "files",
      data: {
        to: "/account/documents",
      },
    },
    {
      type: MenuItemType.routerLink,
      children: "FAQs",
      icon: "questionCircle",
      data: {
        to: "/account/user-faqs",
      },
    },
    {
      type: MenuItemType.routerLink,
      children: "Manage account",
      icon: "toggleOn",
      data: {
        to: "/account/manage-account",
      },
    }
  );

  return items;
};

export const getMenuSections: (props: Props) => MenuSection[] = ({
  userProfile,
  incomeTaxReturns,
}) => {
  const settingsSection = {
    title: "Settings",
    items: getSettingsSection(userProfile),
  };
  const resetCardSection = {
    title: "Reset card",
    items: getCardSection(userProfile),
  };
  const supportSection = {
    title: "Support",
    items: getSupportSection(incomeTaxReturns),
  };

  const sections: MenuSection[] = [settingsSection];
  if (userProfile.eligibleForCard || userProfile.currentCardAccount) {
    sections.push(resetCardSection);
  }
  sections.push(supportSection);

  return sections;
};
